import {Game} from "../../Game";
import {Symbol} from "../Symbol";
import {IMonsterWalk} from "../../server/IData";
import {gsap} from "gsap";

export class CollectAreaHighlightView extends PIXI.Container {

    private _highlightContainers: PIXI.Container[];

    constructor() {
        super();
        this.name = "CollectAreaHighlightView";

        this.init();
    }

    public showHighlightEdges(pathArr: IMonsterWalk[], monsterName: string): gsap.core.Timeline {
        const monsterIdx: number = Game.MONSTER_NAME_TO_IDX[monsterName];

        this._highlightContainers[monsterIdx].alpha = 0;
        this._highlightContainers[monsterIdx].visible = true;

        pathArr.forEach((curr: IMonsterWalk, idx: number) => {
            let [left, right, top, bottom] = [true, true, true, true];
            const reelsSymName: string = Game.model.gameData.evaluatedArea[pathArr[idx].x][pathArr[idx].y],
                symbol: Symbol = Game.reelsView.reelModels[pathArr[idx].x].getSymbolAt(pathArr[idx].y, reelsSymName);

            pathArr.forEach((point: IMonsterWalk) => {
                // left edge skip
                if (curr.y === point.y && curr.x - 1 === point.x) {
                    left = false;
                }
                // right edge skip
                if (curr.y === point.y && curr.x + 1 === point.x) {
                    right = false;
                }
                // top edge skip
                if (curr.x === point.x && curr.y - 1 === point.y) {
                    top = false;
                }
                // bottom edge skip
                if (curr.x === point.x && curr.y + 1 === point.y) {
                    bottom = false;
                }
            });

            if (symbol){
                this.createHighlightOutline(symbol.x, symbol.y, left, right, top, bottom, monsterIdx);
            }
        });

        const tl: gsap.core.Timeline = gsap.timeline();

        tl.to(this._highlightContainers[monsterIdx], {
            duration: 0.2,
            alpha: 1
        });

        return tl;
    }

    public hideHighlightEdges(monsterName: string): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            idx: number = Game.MONSTER_NAME_TO_IDX[monsterName];

        tl.to(this._highlightContainers[idx], {
            duration: 0.1,
            alpha: 0
        });
        tl.add(() => {
            this._highlightContainers[idx].removeChildren();
            this._highlightContainers[idx].visible = false;
            this._highlightContainers[idx].alpha = 1;
        });

        return tl;
    }

    public onGridExpansion(xOffset: number, yOffset: number): void {
        this._highlightContainers.forEach((container: PIXI.Container) => {
            container.position.set(Game.REEL_AREA_X - xOffset, Game.REEL_AREA_Y - yOffset);
        });
    }

    private createHighlightOutline(x: number, y: number, left: boolean, right: boolean, top: boolean, bottom: boolean, monsterIdx: number): void {
        const lineGraphics: PIXI.Graphics = new PIXI.Graphics(),
            rectGraphics: PIXI.Graphics = new PIXI.Graphics();
        let highlightColor,
            fillColor;

        switch (monsterIdx) {
            case 0:
                highlightColor = 0x910000;
                fillColor = 0xbf0000;
                break;
            case 1:
                highlightColor = 0x3f6b0c;
                fillColor = 0x4d6810;
                break;
            case 2:
                highlightColor = 0x0c503b;
                fillColor = 0x1a6e5c;
                break;
            case 3:
                highlightColor = 0x1b337f;
                fillColor = 0x2d3258;
                break;
            default:
                highlightColor = 0xffffff;
                fillColor = 0xffffff;
                break;
        }

        this._highlightContainers[monsterIdx].addChild(lineGraphics, rectGraphics);

        this.drawLine(lineGraphics, x - (Game.SYMBOL_WIDTH / 2), y - (Game.SYMBOL_HEIGHT / 2), [top, right, bottom, left], highlightColor);

        this.drawSymbolFill(rectGraphics, x - (Game.SYMBOL_WIDTH / 2), y - (Game.SYMBOL_HEIGHT / 2), fillColor, 0.2);
    }

    private drawLine(target: PIXI.Graphics, x: number, y: number, borders: boolean[], highlightColor: number): void {
        target.lineStyle(4, highlightColor);
        target.moveTo(x, y);

        for (let i: number = 0; i < 4; i++) {
            // 0 -> top (move x), 1 -> right (move y), 2 -> bottom (move -x), 3 -> left (move -y)

            if (i === 0) {
                x = x + (Game.SYMBOL_WIDTH);
            } else if (i === 1) {
                y = y + (Game.SYMBOL_HEIGHT);
            } else if (i === 2) {
                x = x - (Game.SYMBOL_WIDTH);
            } else if (i === 3) {
                y = y - (Game.SYMBOL_HEIGHT);
            }

            if (borders[i]) {
                target.lineTo(x, y);
            }

            target.moveTo(x, y);
        }
    }

    private drawSymbolFill(target: PIXI.Graphics, x: number, y: number, fillColor: number, alpha: number): void {
        target.beginFill(fillColor, alpha);
        target.drawRect(x, y, Game.SYMBOL_WIDTH, Game.SYMBOL_HEIGHT);
    }

    private init(): void {
        this._highlightContainers = [];

        for (let ctr: number = 0; ctr < Game.MONSTER_SYMBOLS.length; ctr++) {
            const container: PIXI.Container = new PIXI.Container();
            container.visible = false;
            container.position.set(Game.REEL_AREA_X, Game.REEL_AREA_Y);

            this._highlightContainers.push(container);
            this.addChild(container);
        }

        if (Game.model.isRestore && Game.hasGridExpanded) {
            const xOffset: number = (Game.scaleFactor === 0.8 ? 35 : 41),
                yOffset: number = (Game.scaleFactor === 0.8 ? 35 : 40);

            this.onGridExpansion(xOffset, yOffset);
        }
    }
}
